import {Controller} from "stimulus";
import Choices from "choices.js";
import colors from "src/admin/config/styles/colors";

// When an async search URL is provided, choices are fetched from the server as
// the user types instead of being pre-rendered. This keeps large pools (1000+
// options) out of the DOM, which Choices.js renders eagerly and re-renders on
// every selection.
const ASYNC_SEARCH_FLOOR = 2;
const ASYNC_DEBOUNCE_MS = 250;

export default class FilterableSelectController extends Controller {
  static values = {
    error: Boolean,
    searchResultLimit: {type: Number, default: 6},
    removeItemButton: {type: Boolean, default: true},
    asyncSearchUrl: {type: String, default: ""},
  };

  connect() {
    /* Don't add choices if it is already connected */
    if (this.element.getAttribute("data-choice") === "active") {
      return;
    }

    const asyncUrl = this.asyncSearchUrlValue;

    /* Snapshot the pre-loaded options before Choices consumes them so they can
       be restored when the search is cleared. */
    if (asyncUrl) {
      this.eagerChoices = this.snapshotEagerChoices();
    }

    const config = {
      shouldSort: false,
      searchResultLimit: this.searchResultLimitValue,
      searchEnabled: asyncUrl ? true : this.element.options.length >= 10,
      removeItemButton: this.removeItemButtonValue,
      itemSelectText: "",
      placeholderValue: this.element.getAttribute("data-placeholder"),
    };

    this.choices = new Choices(this.element, config);

    if (asyncUrl) {
      this.setupAsyncSearch();
    }

    this.scrollToSelectedChoice();

    if (this.errorValue) {
      this.element.parentElement.style.borderColor = colors.warning.DEFAULT;
    }

    this.element.addEventListener("change", () => {
      if (this.element.value) {
        this.element.classList.add("has-value");
      } else {
        this.element.classList.remove("has-value");
      }
    });
  }

  disconnect() {
    clearTimeout(this.asyncSearchTimeout);
    clearTimeout(this.asyncRestoreTimeout);
    this.element.removeEventListener("addItem", this.handleAsyncItemAdded);
  }

  /**
    * Ensures the selected choice is visible and highlighted when the dropdown is shown.
    */
  scrollToSelectedChoice() {
    this.choices.passedElement.element.addEventListener("showDropdown", () => {
      const id = this.choices.getValue().choiceId;
      const option = this.choices.dropdown.element.querySelector(`[data-id="${id}"]`);

      if (!option) {
        return;
      }

      this.choices.choiceList.scrollToChoice(option, 1);
      this.choices._highlightChoice(option);
    }, false);
  }

  setupAsyncSearch() {
    this.showingEagerChoices = true;
    this.asyncSearchToken = 0;
    this.element.addEventListener("addItem", this.handleAsyncItemAdded);

    /* The Choices `search` event skips empty values (early-return guard in
       _handleSearch), so we'd miss the signal to restore eager choices when
       the input is cleared. Patching _handleSearch is the only hook that
       runs on every keyup, including the empty-value case. */
    const original = this.choices._handleSearch.bind(this.choices);
    this.choices._handleSearch = value => {
      original(value);
      this.handleAsyncQuery(value || "");
    };
  }

  /* Selecting a result clears the search box programmatically (no keyup fires),
     so restore the pre-loaded groups once Choices finishes its own clearInput(). */
  handleAsyncItemAdded = () => {
    clearTimeout(this.asyncSearchTimeout);
    this.asyncSearchToken += 1;
    clearTimeout(this.asyncRestoreTimeout);
    this.asyncRestoreTimeout = setTimeout(() => this.restoreEagerChoices());
  };

  handleAsyncQuery(value) {
    const query = value.trim();
    clearTimeout(this.asyncSearchTimeout);
    if (query.length >= ASYNC_SEARCH_FLOOR) {
      this.asyncSearchTimeout = setTimeout(() => this.performAsyncSearch(query), ASYNC_DEBOUNCE_MS);
    } else {
      this.asyncSearchToken += 1;
      this.restoreEagerChoices();
    }
  }

  performAsyncSearch(query) {
    const token = (this.asyncSearchToken += 1);
    const url = new URL(this.asyncSearchUrlValue, window.location.origin);
    url.searchParams.set("q", query);
    this.choices.getValue(true).forEach(id => url.searchParams.append("selected_ids[]", id));

    fetch(url.toString(), {headers: {Accept: "application/json"}, credentials: "same-origin"})
      .then(response => (response.ok ? response.json() : null))
      .then(choices => {
        if (!choices) { return; } // server error — leave current choices untouched
        if (token !== this.asyncSearchToken) {
          return; // a newer search (or a clear) superseded this one
        }
        if (this.currentSearchValue().length < ASYNC_SEARCH_FLOOR) {
          return; // input was cleared while the request was in flight
        }

        this.showingEagerChoices = false;
        this.choices.setChoices(choices, "value", "label", true);
      })
      .catch(() => {
        // Network errors leave the currently shown choices untouched.
      });
  }

  restoreEagerChoices() {
    if (this.showingEagerChoices) {
      return;
    }

    this.showingEagerChoices = true;
    this.choices.setChoices(this.eagerChoices, "value", "label", true);
  }

  currentSearchValue() {
    return this.choices?.input?.element?.value?.trim() || "";
  }

  snapshotEagerChoices() {
    const groups = Array.from(this.element.querySelectorAll("optgroup"));

    if (groups.length > 0) {
      return groups
        .map(group => ({
          label: group.label,
          choices: this.optionsToChoices(group.querySelectorAll("option")),
        }))
        .filter(group => group.choices.length > 0);
    }

    return this.optionsToChoices(this.element.querySelectorAll("option"));
  }

  optionsToChoices(options) {
    return Array.from(options)
      .filter(option => option.value)
      .map(option => ({value: option.value, label: option.textContent.trim()}));
  }
}
